import { useEffect, useState } from 'react'
import { APPLICATION_PREFERENCES } from '../../utils/constants'
import { ControlledDropDown, CustomButton, FormInput } from '@ui-kit'
import { useForm } from 'react-hook-form'
import {
  EditType,
  GeneralSettingsForm1Type,
  ShiftDetails,
  RootObject,
  Timezone,
} from '../../types/applicationPreferences.types'
import {
  addToaster,
  DataTypeStore,
  DropdownType,
  getAllTimeZones,
  hideLoader,
  showLoader,
} from '@app-store'
import { APPLICATION_PREFERENCES_STRINGS } from '../../utils/strings'
import { convertISOToTime } from '../../utils/helperFunctions'
import {
  addGeneralSettingsData,
  getGeneralSettingsData,
  updateGeneralSettingsData,
} from '../../apis/applicationPreferences.api'
import { v4 as uuid } from 'uuid'
import ShiftTiming from './ShiftTiming'
import { useAppDispatch } from '../../utils/agent.store'

const GeneralSettings = () => {
  const [generalSettingsData, setGeneralSettingsData] = useState<
    RootObject | undefined
  >()
  const dispatch = useAppDispatch()
  const generalSettingsForm1: GeneralSettingsForm1Type = {
    maximum_cases_per_agent: '',
    minimum_cases_per_agent: '',
    time_zone: '',
  }
  const [allTimeZones, setAllTimeZones] = useState<DropdownType<string>[]>([])
  const [allTimeZonesApiData, setAllTimeZonesApiData] = useState<Timezone[]>([])
  const [createGeneralSettings, setCreateGeneralSettings] = useState(true)
  const [editState, setEditState] = useState<EditType>({
    isEdit: false,
    index: undefined,
    id: undefined,
  })
  const [shortKey, setShortKey] = useState('')
  const [childSetData, setChildSetData] = useState<
    ((data: DataTypeStore[]) => void) | null
  >(null)
  const {
    register: registerForm1,
    control: controlForm1,
    handleSubmit: handleSubmitForm1,
    setValue: setValueForm1,
    watch: watchForm1,
    formState: { errors: errorsForm1 },
  } = useForm({
    defaultValues: generalSettingsForm1,
  })

  const fetchDataCompleteGeneralSettings = async () => {
    try {
      dispatch(showLoader())
      const generalSettings = await getGeneralSettingsData()
      if (generalSettings?.data) {
        setCreateGeneralSettings(false)
        setGeneralSettingsData(generalSettings.data)
        setValueForm1(
          'maximum_cases_per_agent',
          generalSettings.data.maximum_cases
        )
        setValueForm1(
          'minimum_cases_per_agent',
          generalSettings.data.minimum_cases
        )
        setValueForm1('time_zone', generalSettings.data.timezone)
        if (allTimeZones.length > 0) {
          setShortKey(
            allTimeZones.find(
              (item) => item.value === generalSettings.data.timezone
            )?.short_key as string
          )
        }
        if (childSetData) {
          childSetData(
            (generalSettings.data?.general_shifts ?? []).map(
              (item: ShiftDetails) => ({
                created_at: item.created_at,
                modified_at: item.modified_at,
                modified_by: item.modified_by,
                id: item.id,
                shift: item.shift,
                start_time: convertISOToTime(item.start_time),
                end_time: convertISOToTime(item.end_time),
                effective_start_date: convertISOToTime(
                  item.effective_start_date
                ),
                effective_end_date: convertISOToTime(item.effective_end_date),
                is_enabled: item.is_enabled,
                is_deleted: item.is_deleted,
                deleted_time: item.deleted_time,
              })
            ) as DataTypeStore[]
          )
        }
      } else {
        setCreateGeneralSettings(true)
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchAllTimeZone = async () => {
    try {
      const response = await getAllTimeZones()
      const array = response.data?.map((item: Timezone) => ({
        value: item.id,
        label: item.timezone_name,
        disabled: false,
        short_key: item.timezone_shortcode,
      }))

      setAllTimeZones(array)
      setAllTimeZonesApiData(response.data)
    } catch (err) {
      console.log(err)
    }
  }
  const handleCancelGeneralSettings = () => {
    if (generalSettingsData) {
      setValueForm1(
        'maximum_cases_per_agent',
        generalSettingsData?.maximum_cases
      )
      setValueForm1(
        'minimum_cases_per_agent',
        generalSettingsData?.minimum_cases
      )
      setValueForm1('time_zone', generalSettingsData?.timezone)
      const result: string =
        allTimeZonesApiData?.find(
          (timezone) => timezone.id === Number(generalSettingsData?.timezone)
        )?.timezone_shortcode ?? ''
      setShortKey(result)
    } else {
      setValueForm1('maximum_cases_per_agent', '')
      setValueForm1('minimum_cases_per_agent', '')
      setValueForm1('time_zone', '')
      setShortKey('')
    }
  }
  const handleTimeZoneChange = (value: DropdownType<string>) => {
    setValueForm1('time_zone', value.value)
    setShortKey(value?.short_key as string)
  }
  const generalSettingsFormSubmit = async (
    formData: GeneralSettingsForm1Type
  ) => {
    try {
      dispatch(showLoader())
      if (editState) {
        const payload = {
          minimum_cases: formData.minimum_cases_per_agent,
          maximum_cases: formData.maximum_cases_per_agent,
          timezone: Number(formData.time_zone),
        }
        if (createGeneralSettings) {
          await addGeneralSettingsData(payload)
          dispatch(
            addToaster({
              message: APPLICATION_PREFERENCES_STRINGS.SETTINGS_ADDED_MESSAGE,
              type: 'success',
              id: uuid(),
            })
          )
          setCreateGeneralSettings(false)
        } else {
          await updateGeneralSettingsData(payload)
          dispatch(
            addToaster({
              message: APPLICATION_PREFERENCES_STRINGS.SETTINGS_UPDATED_MESSAGE,
              type: 'success',
              id: uuid(),
            })
          )
        }
        await fetchDataCompleteGeneralSettings()
      }
    } catch (error) {
      console.error(error)
      dispatch(
        addToaster({
          message: APPLICATION_PREFERENCES_STRINGS.SETTINGS_ADDED_ERROR_MESSAGE,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }
  useEffect(() => {
    fetchDataCompleteGeneralSettings()
  }, [allTimeZones])
  useEffect(() => {
    fetchAllTimeZone()
  }, [])
  return (
    <div className="font-primary flex flex-col gap-3">
      <div className="font-bold text-base font-primary">
        <span>{APPLICATION_PREFERENCES.GENERAL_SETTINGS}</span>
      </div>
      <form onSubmit={handleSubmitForm1(generalSettingsFormSubmit)}>
        <div className="flex flex-col">
          <div className="flex grid-cols-2 justify-between">
            <h2 className="text-base col-span-1 font-primary font-bold pt-3">
              {APPLICATION_PREFERENCES.CASE_ASSIGNMENT_CONFIGURATION}
            </h2>
            <div className="flex justify-between items-center gap-4">
              <div className="flex items-center justify-center">
                <CustomButton
                  color="primary"
                  isFilled={false}
                  className="h-[2.375rem]"
                  onClick={handleCancelGeneralSettings}
                  dataQaId="cancelGeneralSettings"
                >
                  <span className=" font-primary">
                    {APPLICATION_PREFERENCES.CANCEL}
                  </span>
                </CustomButton>
              </div>
              <div className="flex items-center justify-center">
                <CustomButton
                  color="primary"
                  isFilled={true}
                  className="h-[2.375rem]"
                  type="submit"
                  dataQaId="saveGeneralSettings"
                >
                  <span className=" font-primary">
                    {APPLICATION_PREFERENCES.SAVE}
                  </span>
                </CustomButton>
              </div>
            </div>
          </div>
          <h2>
            {
              APPLICATION_PREFERENCES_STRINGS.CASE_ASSIGNMENT_CONFIGURATION_MESSAGE
            }
          </h2>
        </div>
        <div className="flex flex-col gap-6 pt-2">
          <div className="grid grid-cols-4 gap-4 custom-shadow p-4">
            <div className="col-span-1">
              <FormInput
                label={APPLICATION_PREFERENCES.MAXIMUM_CASES}
                placeholder="--"
                errors={errorsForm1}
                name={'maximum_cases_per_agent'}
                register={registerForm1}
                validation={{
                  required: APPLICATION_PREFERENCES_STRINGS.REQUIRED_FIELD,
                  validate: {
                    isPositive: (value) =>
                      parseFloat(value) > 0 ||
                      APPLICATION_PREFERENCES_STRINGS.POSITIVE_VALUE,
                  },
                }}
                type="number"
                dataQaId="maximumCasesGeneralSettings"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                label={APPLICATION_PREFERENCES.MINIMUM_CASES}
                placeholder="--"
                errors={errorsForm1}
                name={'minimum_cases_per_agent'}
                register={registerForm1}
                validation={{
                  required: APPLICATION_PREFERENCES_STRINGS.REQUIRED_FIELD,
                  validate: {
                    isPositive: (value) =>
                      parseFloat(value) > 0 ||
                      APPLICATION_PREFERENCES_STRINGS.POSITIVE_VALUE,
                    isLessThanMax: (value) =>
                      parseFloat(value) <=
                        parseFloat(watchForm1('maximum_cases_per_agent')) ||
                      APPLICATION_PREFERENCES_STRINGS.MINIMUM_CANNOT_EXCEED_MAXIMUM,
                  },
                }}
                type="number"
                dataQaId="minimumCasesGeneralSettings"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col pt-2">
          <div className="flex grid-cols-2 justify-between">
            <h2 className="text-base col-span-1 font-primary font-bold pt-1">
              {APPLICATION_PREFERENCES.SHIFT_TIME_ZONE}
            </h2>
          </div>
          <h2>{APPLICATION_PREFERENCES_STRINGS.SHIFT_TIME_ZONE_MESSAGE}</h2>
        </div>
        <div className="flex flex-col gap-6 pt-2">
          <div className="grid grid-cols-4 gap-4 custom-shadow p-4">
            <div className="col-span-1">
              <ControlledDropDown
                options={allTimeZones ?? []}
                name="time_zone"
                label={APPLICATION_PREFERENCES.TIME_ZONE}
                onChange={(value: DropdownType<string>) =>
                  handleTimeZoneChange(value)
                }
                errors={errorsForm1}
                control={controlForm1}
                required={true}
                isSearchable={true}
                dataQaId="timeZonesGeneralSettings"
              />
            </div>
          </div>
        </div>
      </form>
      <ShiftTiming
        setEditState={setEditState}
        editState={editState}
        provideSetData={setChildSetData}
        shortKey={shortKey}
      />
    </div>
  )
}

export default GeneralSettings
